import { Card, EmptyState, Modal, SearchInput, SpinnerIcon } from '@bp/ui-components';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebounceCallback } from 'usehooks-ts';
import { naturalStringSort } from '../../../utils/stringHelper';
import { CardPicker } from '../CardPicker/CardPicker';
import { FindingsPanel } from '../FindingsPanel/FindingsPanel';
import { SolverRunBanner } from '../SolverRunBanner/SolverRunBanner';
import { useTimetableStore } from '../TimetableProvider';
import styles from './GridActionArea.module.scss';

export const GridActionArea: FC = observer(() => {
  const { t } = useTranslation();

  const pinboardStore = useTimetableStore();

  const [loadingModalOpen, setLoadingModalOpen] = useState<boolean>(false);
  const [searchString, setSearchString] = useState<string>('');
  const setSearchDebounced = useDebounceCallback((v: string) => pinboardStore.setCardSearchString(v), 200);

  // sort "Aus" before 10a
  const selectedRows = pinboardStore.selectedRows
    .map((row) => row.label)
    .sort((a, b) => {
      return naturalStringSort(a, b);
    })
    .join(', ');

  function getBoardContextString(): string {
    let contextString = '';
    if (pinboardStore.context === 'classes') {
      if (selectedRows)
        contextString = t('pinboard.showSelectionFor', {
          type: t('classes.title', { count: 1 }),
        });
      else
        contextString = t('pinboard.showContextFor', {
          context: t('classes.title', { count: 2 }),
        });
    }
    if (pinboardStore.context === 'teachers') {
      if (selectedRows)
        contextString = t('pinboard.showSelectionFor', {
          type: t('persons.title', { count: 2 }),
        });
      else
        contextString = t('pinboard.showContextFor', {
          context: t('persons.title', { count: 2 }),
        });
    }
    if (pinboardStore.context === 'rooms') {
      if (selectedRows)
        contextString = t('pinboard.showSelectionFor', {
          type: t('rooms.title', { count: 2 }),
        });
      else
        contextString = t('pinboard.showContextFor', {
          context: t('rooms.title', { count: 2 }),
        });
    }
    return contextString;
  }

  // Background solver run takes over the card area like control / Timefold-analyze do,
  // so the user sees the live status (spinner + algo + elapsed + gap) instead of the
  // (read-only) card picker while a solver job is in flight.
  const solverRunningInBackground = pinboardStore.solverStore.jobRunning && !pinboardStore.solverStore.solverWizardOpen;

  return (
    <>
      <Card
        className={styles['grid-action-area']}
        wrapperClass={classNames(styles.wrapper, {
          // Background solver run reuses the slot but stays visually flat — we
          // don't want the scale/shadow highlight that signals "action needed"
          // (which control / Timefold-analyze use because they surface diagnostics).
          [styles['control-mode']]: pinboardStore.controlMode.isActive || pinboardStore.timefoldAnalyzeMode.isActive,
        })}
        contentPadding='none'
        fitContent
      >
        {solverRunningInBackground ? (
          <SolverRunBanner />
        ) : pinboardStore.controlMode.isActive || pinboardStore.timefoldAnalyzeMode.isActive ? (
          <FindingsPanel className={styles.control} />
        ) : (
          <>
            <div className={styles.header}>
              <div className={styles.selection}>
                <div className={styles['board-context']}>{getBoardContextString()}</div>
                <div className={styles['selected-rows']}>{selectedRows}</div>
              </div>
              <div className={styles.actions}>
                <SearchInput
                  name='search-string'
                  isOnWhite
                  value={searchString}
                  onChange={(event) => {
                    setSearchString(event.target.value);
                    setSearchDebounced(event.target.value);
                  }}
                  placeholder={t('pinboard.actions.searchCards')}
                  className={styles['search-input']}
                />
              </div>
            </div>
            <CardPicker searchString={searchString} />
          </>
        )}
      </Card>

      <Modal
        isOpen={loadingModalOpen}
        onRequestClose={() => setLoadingModalOpen(false)}
        hideHeader
        hideFooter
        width='s'
        hideCloseButton
        shouldCloseOnEsc={false}
        shouldCloseOnOverlayClick={false}
      >
        <EmptyState
          size='small'
          iconColor='var(--color-grey)'
          title={t('pinboard.timetableDownloadHint')}
          icon={<SpinnerIcon className='svg-icon spinning' />}
        />
      </Modal>
    </>
  );
});
